import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex } from "theme-ui";

import H5 from "components/typography/H5";
import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import BasicButton from "components/forms/BasicButton";
import TextInput from "components/forms/TextInput";
import { themevals } from "theme/themevals";
import { useToast } from "components/toast";

import { getUpdateClientTemplates } from "api/ServicePlans";

/**
 * §3.9 #19 — Update Client template composer. Design language mirrors
 * the §3.9 #17 Corrigo paste-export panel (Session 39) — same panel
 * shell, same "plan name in the header" cue, same Copy affordance. No
 * dedicated mockup for this one; the pattern is inherited.
 *
 * Staff-only, plan-tagged projects only. Non-plan projects (the ~284
 * existing clients' projects) never see this panel because the
 * containing view.tsx conditional gates it behind
 * `project.originQuoteRequest?.servicePlanId`.
 *
 * Local edit before copy. Templates come back with server-side variable
 * substitution already applied, so the initial textarea reflects the
 * final message. Staff can hand-edit before copying — the composer is
 * paste-ready, not send-through-JSCRM (JSCRM doesn't send FM updates;
 * they go through CorrigoPro). That intent is why "Copy to clipboard"
 * is the only action verb here, not "Send."
 *
 * Unresolved-variable banner: if the selected template references a
 * placeholder that has no value on the current project (e.g. the
 * template mentions {{completedDate}} but the project isn't complete
 * yet), the panel shows a soft warning before letting staff copy. The
 * substitution still fills in what it can and leaves the rest as literal
 * `{{key}}` in the textarea so a rushed copy-paste doesn't ship the
 * placeholder itself to the FM.
 */
export default function UpdateClientPanel({ project }: { project: any }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [customBody, setCustomBody] = useState<string>("");
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const res = await getUpdateClientTemplates({ data: { id: project.id } });
      setData(res);
      if (res?.templates?.length) {
        applyTemplate(res.templates[0], res.variables);
        setSelectedId(res.templates[0].id);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line
  }, [project?.id]);

  const applyTemplate = (template: any, variables: Record<string, string>) => {
    const rendered = renderTemplate(template.body, variables);
    setCustomBody(rendered);
    setEdited(false);
  };

  const onSelectTemplate = (template: any) => {
    if (edited && !window.confirm("Discard your edits and switch templates?")) return;
    setSelectedId(template.id);
    applyTemplate(template, data?.variables ?? {});
  };

  const templateUnresolvedForSelected = useMemo(() => {
    if (!data?.templates || !selectedId) return [];
    const selected = data.templates.find((t: any) => t.id === selectedId);
    if (!selected) return [];
    const placeholderRe = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    const referenced = new Set<string>();
    let m: RegExpExecArray | null;
    while ((m = placeholderRe.exec(selected.body)) !== null) referenced.add(m[1]);
    const vars = data.variables || {};
    return Array.from(referenced).filter((k) => !vars[k] || vars[k].trim() === "");
  }, [data, selectedId]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(customBody);
      toast.add("Update copied to clipboard.", "var(--theme-ui-colors-success)");
    } catch {
      toast.add("Clipboard blocked.", "var(--theme-ui-colors-danger)");
    }
  };

  if (loading) return null;
  if (!data?.templates?.length) return null;

  return (
    <Box
      sx={{
        marginTop: "24px",
        border: "1px solid",
        borderColor: "inputOutline",
        borderRadius: "8px",
        overflow: "hidden",
        background: "white",
      }}
    >
      <Box sx={{ padding: "16px 20px", background: "#FAFAF8", borderBottom: "1px solid", borderColor: "inputOutline" }}>
        <H5 fontFamily="Poppins" fontWeight={600} margin={0} padding={0} fontSize={"14px"}>
          Copy status update
        </H5>
        <Subtext fontSize={"12px"} fontFamily={"Poppins"} margin={0}>
          {data.planName ? `${data.planName} · ` : ""}Copy a status update to paste into CorrigoPro.
        </Subtext>
      </Box>

      <Box sx={{ padding: "16px 20px" }}>
        <Subtext fontSize={"11px"} fontFamily={"Poppins"} fontWeight={600} margin={0} color={themevals.colors.dkgrey}>
          TEMPLATE
        </Subtext>
        <Flex sx={{ gap: "8px", flexWrap: "wrap", marginTop: "6px" }}>
          {data.templates.map((t: any) => (
            <Box
              key={t.id}
              as="button"
              onClick={() => onSelectTemplate(t)}
              sx={{
                background: selectedId === t.id ? themevals.colors.black : "transparent",
                color: selectedId === t.id ? "#fff" : themevals.colors.dkgrey,
                border: "1px solid",
                borderColor: selectedId === t.id ? themevals.colors.black : "inputOutline",
                borderRadius: "999px",
                padding: "5px 12px",
                cursor: "pointer",
                fontFamily: "Poppins",
                fontSize: "12px",
                fontWeight: 500,
              }}
            >
              {t.label}
            </Box>
          ))}
        </Flex>

        {templateUnresolvedForSelected.length > 0 && (
          <Box
            sx={{
              marginTop: "12px",
              padding: "8px 12px",
              background: "#FFF8E1",
              border: "1px solid #E7CC7A",
              borderRadius: "6px",
            }}
          >
            <Subtext fontSize={"11px"} fontFamily={"Poppins"} margin={0} color={themevals.colors.dkgrey}>
              This template references{" "}
              <Span fontWeight={600} color={themevals.colors.black}>
                {templateUnresolvedForSelected.map((k) => `{{${k}}}`).join(", ")}
              </Span>{" "}
              — those values aren&apos;t set on this project yet. Fill them in before copying.
            </Subtext>
          </Box>
        )}

        <Box sx={{ marginTop: "14px" }}>
          <TextInput
            type="textarea"
            name="updateClientBody"
            label="Message"
            rows={5}
            value={customBody}
            onChange={(val: any) => {
              setCustomBody(val);
              setEdited(true);
            }}
          />
        </Box>

        <Flex sx={{ marginTop: "14px", justifyContent: "flex-end" }}>
          <BasicButton type="button" onClick={copyToClipboard} keepText={true}>
            Copy to clipboard
          </BasicButton>
        </Flex>
      </Box>
    </Box>
  );
}

function renderTemplate(body: string, variables: Record<string, string>): string {
  return body.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_full, key: string) => {
    const val = variables[key];
    return val && val.trim() !== "" ? val : `{{${key}}}`;
  });
}
